import React, { useState, useEffect, useMemo } from 'react'
import { TourSelection } from '../../components/TourSelection/TourSelection'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { IS_PERIOD_CONTRACT_SELECTION_DISABLED } from '../../config/featureFlags'
import { AdvancedNavigationGuard } from '../../components/AdvancedNavigationGuard'
import { useUser, useTours, useMyContractedTourIds } from '../../hooks'

export const JobPostSecondPageView = () => {
	const location = useLocation()
	const navigate = useNavigate()

	useEffect(() => {
		if (!IS_PERIOD_CONTRACT_SELECTION_DISABLED) return
		if (sessionStorage.getItem('JobPostContractType') === 'period') {
			navigate('/posts/create/type', { replace: true })
		}
	}, [navigate])

	const BetterMatchingModal = ({ isOpen, onClose, onConfirm }) => {
		if (!isOpen) return null

		return (
			<div className="modal-overlay">
				<div className="modal-content">
					<div className="modal-title">잠깐만요! 더 나은 매칭을 위해</div>
					<div className="modal-message">
						대회를 지금 지정하면 지원자가 내 일정을
						<br />
						정확히 알고 지원할 수 있어 매칭 확률이 훨씬 높아져요.
					</div>
					<div className="modal-buttons">
						<Link
							to="/posts/create/terms"
							className="modal-button secondary"
							onClick={onClose}>
							<div>다음에</div>
						</Link>
						<button className="modal-button primary" onClick={onConfirm}>
							대회 선택하기
						</button>
					</div>
				</div>
			</div>
		)
	}

	const [toursCheckedByChild, setToursCheckedByChild] = useState(() => {
		try {
			const raw = sessionStorage.getItem('JobPostSelectedTours')
			const parsed = raw ? JSON.parse(raw) : []
			return Array.isArray(parsed) ? parsed : []
		} catch {
			return []
		}
	})
	const ContractType = sessionStorage.getItem('JobPostContractType')
	const currentYear = useMemo(() => {
		try {
			const raw = sessionStorage.getItem('JobPostCurrentYear')
			return raw != null ? JSON.parse(raw) : new Date().getFullYear()
		} catch {
			return new Date().getFullYear()
		}
	}, [])

	const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(true)
	const [isModalOpen, setIsModalOpen] = useState(false)

	const { data: user } = useUser()
	const { data: myContractedTourIdsRaw } = useMyContractedTourIds()
	const myContractedTourIds = myContractedTourIdsRaw ?? []

	const jobPostGender = useMemo(() => {
		const tt = user?.tourPro?.tour_type
		if (tt === 'KLPGA') return 'FEMALE'
		if (tt === 'KPGA') return 'MALE'
		return undefined
	}, [user?.tourPro?.tour_type])

	const { data: toursData } = useTours(jobPostGender)

	const previousSelectedTours = useMemo(() => {
		if (!toursData || !toursCheckedByChild.length) {
			return { tours: [] }
		}
		const findTourById = (tourId) => {
			const domesticTour = toursData.domestic?.find((t) => t.id === tourId)
			if (domesticTour) return domesticTour
			if (toursData.international?.tours) {
				const allInternationalTours = Object.values(toursData.international.tours).flat()
				return allInternationalTours.find((t) => t.id === tourId) || null
			}
			return null
		}
		return { tours: toursCheckedByChild.map(findTourById).filter(Boolean) }
	}, [toursData, toursCheckedByChild])

	useEffect(() => {
		if (ContractType === 'long') {
			setIsNextButtonDisabled(toursCheckedByChild.length < 2)
		} else {
			setIsNextButtonDisabled(toursCheckedByChild.length === 0)
		}
		sessionStorage.setItem('JobPostSelectedTours', JSON.stringify(toursCheckedByChild))
	}, [toursCheckedByChild, ContractType])

	// 이미 계약 체결된 대회는 선택 해제 (목록 로드 후)
	useEffect(() => {
		if (!myContractedTourIds.length) return
		setToursCheckedByChild((prev) => prev.filter((id) => !myContractedTourIds.includes(id)))
	}, [myContractedTourIds])

	const handleSelectedToursFromChild = (selectedIds) => {
		setToursCheckedByChild(selectedIds)
	}

	const handleLaterButtonClick = (e) => {
		e.preventDefault()
		setIsModalOpen(true)
	}

	const handleModalClose = () => {
		setIsModalOpen(false)
		sessionStorage.setItem('JobPostSelectedTours', JSON.stringify([]))
		setToursCheckedByChild([])
	}

	const handleModalConfirm = () => {
		setIsModalOpen(false)
	}

	const tourSelectionProps = {
		mode: 'offer',
		TourCount: ContractType === 'long',
		selectOnlyOne: ContractType === 'short',
		onSelectedToursChange: handleSelectedToursFromChild,
		filterYear: currentYear,
		upcomingOnly: false,
		previousSelectedTours,
		disabledTourIds: myContractedTourIds,
	}

	const renderTourSelection = () => {
		if (ContractType === 'short') {
			return (
				<AdvancedNavigationGuard>
					<div className="body">
						<TourSelection key={location.key} {...tourSelectionProps} />
						<div className="next-select-buttons">
							<Link to="/posts/create/type">
								<div className="before">이전</div>
							</Link>
							<Link
								to="/posts/create/terms"
								onClick={(e) => isNextButtonDisabled && e.preventDefault()}>
								<div
									className={
										isNextButtonDisabled ? 'next disabled-link' : 'next'
									}>
									다음
								</div>
							</Link>
						</div>
					</div>
				</AdvancedNavigationGuard>
			)
		}
		if (ContractType === 'long') {
			return (
				<AdvancedNavigationGuard>
					<div className="body">
						<TourSelection key={location.key} {...tourSelectionProps} />
						<div className="next-select-buttons">
							<Link to="/posts/create/type">
								<div className="before">이전</div>
							</Link>
							<Link
								to="/posts/create/terms"
								onClick={(e) => isNextButtonDisabled && e.preventDefault()}>
								<div
									className={
										isNextButtonDisabled ? 'next disabled-link' : 'next'
									}>
									다음
								</div>
							</Link>
						</div>
					</div>
				</AdvancedNavigationGuard>
			)
		}
		if (ContractType === 'period') {
			return (
				<AdvancedNavigationGuard>
					<div className="body">
						<TourSelection key={location.key} {...tourSelectionProps} />
						<div className="next-select-buttons">
							<Link to="/" onClick={handleLaterButtonClick}>
								<div className="skip">다음에 할게요</div>
							</Link>
							<Link
								to="/posts/create/terms"
								onClick={(e) => isNextButtonDisabled && e.preventDefault()}>
								<div
									className={
										isNextButtonDisabled ? 'next disabled-link' : 'next'
									}>
									다음
								</div>
							</Link>
						</div>
					</div>
				</AdvancedNavigationGuard>
			)
		}
		return null
	}

	return (
		<div className="job-post-second-page-container">
			<div className="title">공고 작성</div>
			<div className="progress-bar"></div>
			{renderTourSelection()}
			<BetterMatchingModal
				isOpen={isModalOpen}
				onClose={handleModalClose}
				onConfirm={handleModalConfirm}
			/>
		</div>
	)
}
